import React, { useState, useEffect } from 'react';
// 1. ✅ Import your apiClient
import apiClient from '../apiClient'; // <-- Adjust path as needed
import { useParams, useNavigate } from 'react-router-dom';

// --- MUI Components ---
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton // 2. ✅ Import IconButton for the back arrow
} from '@mui/material';

// --- MUI Icons ---
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material'; // 3. ✅ Import ArrowBackIcon

export default function JobForm() {
  const [formData, setFormData] = useState({
    website: 'horizonHauliers',
    title: '',
    location: '',
    type: 'Full-time',
    workplace: 'Onsite',
    department: 'Operations',
    summary: '',
    responsibilities: '',
    requirements: '',
  });

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // --- Data Fetching (Unchanged) ---
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      setError(null);
      apiClient.get(`/careers/jobs/${id}`)
        .then(response => {
          const { data } = response;
          setFormData({
            ...data,
            responsibilities: data.responsibilities.join('\n'),
            requirements: data.requirements.join('\n'),
          });
        })
        .catch(err => {
          console.error(err);
          setError('Failed to load job data. Please try again.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditMode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // --- Form Submission (Unchanged) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    const postData = {
      ...formData,
      responsibilities: formData.responsibilities.split('\n').filter(item => item),
      requirements: formData.requirements.split('\n').filter(item => item),
    };

    try {
      if (isEditMode) {
        await apiClient.put(`/careers/jobs/${id}`, postData);
      } else {
        await apiClient.post('/careers/jobs', postData);
      }
      navigate('/admin/careers'); 
      
    } catch (err) {
      console.error('Error saving job:', err);
      let errorMsg = 'Failed to save job. Please check your entries and try again.';
      if (err.response) {
        if (err.response.status === 401 || err.response.status === 403) {
          errorMsg = 'Authentication failed. Please log out and log in again.';
        } else {
          errorMsg = `Error: ${err.response.data.message || err.message}`;
        }
      } else if (err.request) {
        errorMsg = 'Network Error: Could not connect to the server.';
      }
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Loading Spinner (Unchanged) ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // --- Main Form Render ---
  return (
    // 4. ✅ Use <Box> to fit cleanly in AdminLayout
    <Box>
      {/* 5. ✅ Add a styled Paper wrapper for consistent "white styling" */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '10px' }}>
        
        {/* 6. ✅ Add the Back Button next to the title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/admin/careers')} aria-label="go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ ml: 1 }}>
            {isEditMode ? 'Edit Job' : 'Create New Job'}
          </Typography>
        </Box>

        {/* --- Display error message if one exists --- */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            
            {/* --- Row 1: Title --- */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                id="title"
                label="Job Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>

            {/* --- Row 2: Website & Department --- */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="website-select-label">Website</InputLabel>
                <Select
                  labelId="website-select-label"
                  id="website"
                  name="website"
                  label="Website"
                  value={formData.website}
                  onChange={handleChange}
                >
                  <MenuItem value="horizonHauliers">Horizon Hauliers</MenuItem>
                  <MenuItem value="horizonEnergies">Horizon Energies</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                id="department"
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
              />
            </Grid>
            
            {/* --- Row 3: Location, Type, Workplace --- */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                id="location"
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                id="type"
                label="Type (e.g., Full-time)"
                name="type"
                value={formData.type}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                id="workplace"
                label="Workplace (e.g., Onsite)"
                name="workplace"
                value={formData.workplace}
                onChange={handleChange}
              />
            </Grid>

            {/* --- Row 4: Summary --- */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                id="summary"
                label="Summary"
                name="summary"
                value={formData.summary}
                onChange={handleChange}
              />
            </Grid>

            {/* --- Row 5: Responsibilities --- */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={5}
                id="responsibilities"
                label="Responsibilities (one per line)"
                name="responsibilities"
                value={formData.responsibilities}
                onChange={handleChange}
              />
            </Grid>

            {/* --- Row 6: Requirements --- */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={5}
                id="requirements"
                label="Requirements (one per line)"
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
              />
            </Grid>

            {/* 7. ✅ Add a Box for the action buttons (Cancel, Save) */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/admin/careers')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {isSubmitting ? 'Saving...' : 'Save Job Post'}
                </Button>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}